import { useEffect, useId, useLayoutEffect, useState } from "react";
import { useFloatingPortal } from "@foris/avocado-suite";

export const useSessionTableTooltip = (disabled: boolean) => {
  const tooltipId = useId();
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [targetVisible, setTargetVisible] = useState(true);
  const isOpen = !disabled && targetVisible && (hovered || focused) && !dismissed;
  const { referenceRef, Portal, update } = useFloatingPortal({
    isOpen,
    strategy: "fixed",
    offset: [0, -8],
    onIsTargetVisible: setTargetVisible,
  });

  // Avocado remounts Portal when it first becomes positioned, resetting its inline coordinates.
  useLayoutEffect(() => {
    if (isOpen) update();
  }, [isOpen, Portal, update]);

  useEffect(() => {
    const trigger = referenceRef.current;
    if (!isOpen || !trigger) return;
    const dismiss = () => setDismissed(true);
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") dismiss();
    };
    trigger.addEventListener("click", dismiss);
    trigger.addEventListener("keydown", onKeyDown);
    return () => {
      trigger.removeEventListener("click", dismiss);
      trigger.removeEventListener("keydown", onKeyDown);
    };
  }, [isOpen, referenceRef]);

  return {
    tooltipId,
    isOpen,
    referenceRef,
    Portal,
    onMouseEnter: () => {
      setHovered(true);
      setDismissed(false);
    },
    onMouseLeave: () => setHovered(false),
    onFocus: () => {
      setFocused(true);
      setDismissed(false);
    },
    onBlur: () => setFocused(false),
  };
};
